import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Alert,
} from "reactstrap";

const normalizePF = (val) => {
  const v = (val ?? "").toString().trim().toLowerCase();
  if (v === "p" || v === "pass") return "p";
  if (v === "f" || v === "fail") return "f";
  return v;
};

const displayCurrentValue = (v) => {
  const s = (v ?? "").toString().trim();
  if (s === "-777") return "PASS";
  if (s === "-999") return "FAIL";
  return s;
};

const toDbValueLegacy = ({ rawInput, type }) => {
  const input = (rawInput ?? "").toString().trim();

  const isStar = (type ?? "").toString().includes("*");
  const isPass = (type ?? "").toString().toLowerCase().includes("pass");

  if (isPass && !isStar) {
    const v = normalizePF(input);
    if (v === "p") return "-777";
    if (v === "f") return "-999";
    return null;
  }

  if (isStar) {
    return input;
  }

  const n = Number(input);
  if (!Number.isFinite(n)) return null;
  return String(n);
};

/**
 * Per-cell Supervisor Override modal.
 *
 * Legacy parity: SupvOverride.vb. P/F validation for pass-type dims, decimal
 * validation for numeric dims, free-text for *-type dims. Converts P→"-777"
 * and F→"-999" on save (legacy sentinel values).
 *
 * UI-consistency rewrite using reactstrap. Props unchanged:
 *   - open, onClose, onConfirm(newDbValue) → returns truthy on success
 *   - context: { jobNum, waveNumber, dnaNum, pieceNum, cellNum, revMajor,
 *               revMinor, type, currentValue }
 */
const SupvOverrideModal = ({ open, onClose, onConfirm, context }) => {
  const [newValue, setNewValue] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewValue("");
    setErr("");
    setSaving(false);
  }, [open, context]);

  const type = useMemo(() => (context?.type ?? "").toString(), [context]);

  const currentDisplay = useMemo(
    () => displayCurrentValue(context?.currentValue),
    [context]
  );

  const placeholder = useMemo(() => {
    return type.toLowerCase().includes("pass") && !type.includes("*")
      ? "Enter P or F"
      : "Enter new value";
  }, [type]);

  const validateLegacy = useCallback(() => {
    const input = (newValue ?? "").toString().trim();
    if (!input) {
      setErr("Please enter a new value.");
      return false;
    }

    const isStar = type.includes("*");
    const isPass = type.toLowerCase().includes("pass");

    if (isPass && !isStar) {
      const v = normalizePF(input);
      if (!(v === "p" || v === "f")) {
        setErr("Please enter only P or F in a pass/fail dimension.");
        return false;
      }
      return true;
    }

    if (!isStar) {
      const n = Number(input);
      if (!Number.isFinite(n)) {
        setErr(
          "Invalid value detected here. Please try again or contact technical support on x1776"
        );
        return false;
      }
    }

    return true;
  }, [newValue, type]);

  const handleInputChange = useCallback(
    (e) => {
      setNewValue(e.target.value);
      if (err) setErr("");
    },
    [err]
  );

  const handleSave = useCallback(async () => {
    if (saving) return;

    setErr("");
    if (!validateLegacy()) return;

    const newDb = toDbValueLegacy({ rawInput: newValue, type });
    if (newDb == null) {
      setErr("Invalid value. Please enter a valid value.");
      return;
    }

    setSaving(true);
    try {
      const ok = await onConfirm?.(newDb);
      if (!ok) {
        setErr(
          "Error in updating this MAFIA entry. Has this entry already been locked? Please try again or contact technical support on x1776."
        );
        return;
      }
      onClose?.();
    } finally {
      setSaving(false);
    }
  }, [saving, validateLegacy, newValue, type, onConfirm, onClose]);

  return (
    <Modal isOpen={!!open} toggle={saving ? undefined : onClose} centered>
      <ModalHeader toggle={saving ? undefined : onClose}>
        <i className="ri-shield-keyhole-line align-bottom me-2 text-warning"></i>
        Supervisor Override
        <Badge color="light" className="ms-2 text-muted">
          Legacy Override
        </Badge>
      </ModalHeader>
      <ModalBody>
        <div className="text-muted small mb-3">
          <div>
            <strong>Job:</strong> {context?.jobNum || "—"} &nbsp;
            <strong>Wave:</strong> {context?.waveNumber ?? "—"}
          </div>
          <div>
            <strong>DNA:</strong> {context?.dnaNum || "—"} &nbsp;
            <strong>Piece:</strong> {context?.pieceNum ?? "—"} &nbsp;
            <strong>Cell:</strong> {context?.cellNum ?? "—"}
          </div>
          <div>
            <strong>Rev:</strong> {context?.revMajor ?? "—"}.
            {context?.revMinor ?? "—"} &nbsp;
            <strong>Type:</strong> {type || "—"}
          </div>
        </div>

        <div className="mb-3">
          <span className="text-muted small">Current Value:</span>{" "}
          <strong className="text-dark">{currentDisplay || "—"}</strong>
        </div>

        <Form onSubmit={(e) => e.preventDefault()}>
          <FormGroup className="mb-0">
            <Label for="supv-override-input" className="small text-muted">
              New Value
            </Label>
            <Input
              id="supv-override-input"
              type="text"
              value={newValue}
              onChange={handleInputChange}
              placeholder={placeholder}
              disabled={saving}
              autoFocus
            />
          </FormGroup>
        </Form>

        {err && (
          <Alert color="danger" className="mt-3 mb-0 py-2 small">
            {err}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <span
                className="spinner-border spinner-border-sm align-middle me-2"
                role="status"
                aria-hidden="true"
              />
              Updating…
            </>
          ) : (
            <>
              <i className="ri-check-line align-bottom me-1"></i>
              Update
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(SupvOverrideModal);
